import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {navigator} from 'lit-element-router';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import store from '../store';
import {setMedia, setSection, toggleSettingsActive} from '../slices/statusSlice';
import close from '../../icons/close';
import overview from '../../icons/overview';
import gear from '../../icons/gear';
import next from '../../icons/next';
import previous from '../../icons/previous';
import specsheet from '../../icons/specsheet';


import {
	selectUnsatisfied,
	selectLightProfileIsSatisfied,
	selectIsSatisfied,
	selectCurrentSection,
	selectCurrentSectionConfig,
	selectSectionConfig,
	selectFinalized
} from '../selectors';
import {routeUrl} from '../util/routeUrl';
import {sectionConfigs} from '../sectionConfig';
import {isSectionSatisfied} from '../util/sectionTabInfo';

@customElement('sections-head')
export class SectionsHead extends connect(store)(navigator(LitElement)) {

	static styles = [
		css`
			:host {}

			.sectionsHead {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: space-between;
				margin: 0 var(--small-padding);
				padding: var(--padding) 0 var(--large-padding) ;
				gap: 10px
			}
			@media (min-width: 768px) {
				.sectionsHead {
					margin: 0 var(--large-padding);
				}
			}

			.toolbar {
				display: flex;
				flex-direction: row;
				gap: 8px;
				margin-left: auto;
			}

			.back {
				height: 23px;
				display: block;
				font-family: var(--font-family-regular);
				font-size: var(--font-size-widget);
				color: var(--color-white);
				text-transform: uppercase;
				letter-spacing: 1px;
				text-decoration: none;
				padding: 0;
			}

			.back svg {
				height: 100%;
				fill: currentColor;
			}

			.settings {
				display: flex;
				position: relative;
			}

			button {
				font-family: var(--font-family-light);
				font-size: var(--font-size-widget);
				color: var(--color-text-widget-title);
				height: var(--nav-button-height);
				width: var(--nav-button-height);
				text-transform: uppercase;
				letter-spacing: 1px;
				background-color: var(--color-widget-background);
				border: none;
				cursor: pointer;
				line-height: 0;
				transition: background-color 0.4s ease-out, color 0.4s ease-out;
			}
			button:hover {
				background-color: var(--color-widget-background-hover);
			}

			@media (min-width: 768px) {
				.price {
					margin-right: var(--large-padding);
				}
			}

			.dashboard {
				margin-right: var(--padding);
				padding: 0;
				color: var(--color-grey4);
				display: none;
			}

			@media (min-width: 768px) {
				.dashboard {
					display: block;
				}
			}

			.active {
				background-color: var(--color-widget-background-selected);
			}


			.specsheet {
				color: var(--color-grey5);
				border: none;
				padding: 0 var(--small-padding);
			}

			.specsheet.active {
				background-color: var(--color-grey2);
				color: var(--color-black);
				cursor: pointer;
			}

			.specsheet.active .badge {
				display: none;
			}

			.desktop-only {
				display: flex;
				margin-left: auto;
			}

			@media (max-width: 768px) {

				.badge {
					background-color: var(--color-warn);
					color: var(--color-white);
					border-radius: 5px;
					font-size: 12px;
					padding: 1px 4px;
					pointer-events: none;
				}

				.badge.satisfied {
					background-color: transparent
				}

				.desktop-only {
					display: none;
				}
			}
		`
	];

	constructor() {
		super();
		// close dashboard when changing to small breakpoint
		window.matchMedia('(max-width: 768px)').addEventListener('change', (e) => {
			if (e.matches) {
				this.closeDashboard();
			}
		});
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.dashboardActive = state.status.dashboardActive;
		this.specsheetActive = state.status.specsheetActive || this.state.status.media === 'summary';
		this.settingsActive = state.status.settingsActive;
		this.isInitialized = state.status.initialized;

		this.featureModelIsSatisfied = selectIsSatisfied(state);

		this.productData = state.productData.productData;
		this.lang = state.status.lang;
		this.region = state.status.region;
		this.mode = state.status.mode;
		this.forceOverview = state.status.forceOverview;
		this.hideOverview = state.status.hideOverview;
		this.models = state.productData.models.models;
		this.productModel = state.configuration.data?.projects[0]?.project?.productModelId;
		this.price = state.price.data;
		this.embed = state.status.embed;
		this.loggedIn = state.userProfile.data.loggedIn;
		this.family = state.status.family;
		this.currentSection = selectCurrentSection(state);
		this.sectionConfig = selectSectionConfig(state);
		this.lastSatisfiedIndex = 0;
		this.sectionConfig.some((section, index) => {
			this.lastSatisfiedIndex = index;
			return !isSectionSatisfied(section.id, state);
		});
		this.sectionTheme = selectCurrentSectionConfig(state)?.theme ?? '';
		this.simpleNavigation = selectCurrentSectionConfig(state)?.simpleNavigation ?? false;
		this.finalized = selectFinalized(state);
	}

	closeDashboard() {
		if (this.dashboardActive) {
			store.dispatch(setMedia({media: 'sections'}));
		}
	}

	toggleDashboard(e) {
		this.navigate(routeUrl({media: this.dashboardActive && !this.forceOverview ? 'sections' : 'overview'}));
	}
	toggleSettings(e) {
		store.dispatch(toggleSettingsActive());
	}
	toggleSpecsheet(e) {
		if (this.specsheetActive) {
			this.navigate(routeUrl({media: this.forceOverview ? 'overview' : 'sections'}));
		} else {
			if (this.state.status.family === 'LightProfile') {
				// TODO: not hardcoded configuration of summary/specsheet component
				this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
			} else {
				this.navigate(routeUrl({media: 'specsheet'}));
			}
		}
	}
	onFinalize() {
		if (this.finalized) {
			// if already finalized, go back to final screen
			store.dispatch(setSection({currentSection: 'finalize'}));
			store.dispatch(setMedia({media: 'sections'}));
		} else {
			// otherwise show the specsheet
			store.dispatch(setSection({currentSection: 'specsheet'}));
			store.dispatch(setMedia({media: 'sections'}));
		}
	}
	previousSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
		const newSection = this.sectionConfig[newIndex].id;

		store.dispatch(setSection({currentSection: newSection}));
		store.dispatch(setMedia({media: 'sections'}));
	}
	nextSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex < this.sectionConfig.length - 1 ? currentIndex + 1 : this.sectionConfig.length - 1;
		const newSection = this.sectionConfig[newIndex].id;

		if (currentIndex < this.sectionConfig.length - 1) {
			store.dispatch(setSection({currentSection: newSection}));
			store.dispatch(setMedia({media: 'sections'}));
		} else {
			if (this.state.status.family === 'LightProfile') {
				// TODO: not hardcoded configuration of summary/specsheet component
				this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
			} else {
				// this.navigate(routeUrl({media: 'specsheet'}));
			}
		}
	}
	lastSection() {
		const lastSection = this.sectionConfig[this.sectionConfig.length - 1].id;
		store.dispatch(setSection({currentSection: lastSection}));
		store.dispatch(setMedia({media: 'sections'}));
	}

	render() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const currentSectionConfig = this.sectionConfig[currentIndex];
		if (currentSectionConfig.noNavigation) return null;

		return html`
			${when(!this.embed,
				() => html`
					<div class="sectionsHead ${this.sectionTheme}">
						${when(!this.simpleNavigation, () => html`
						${when(
							!this.specsheetActive && !(this.family === 'Start' && !this.loggedIn),
							() => html`
								<sections-tabs></sections-tabs>`)
						}
						<div class="toolbar desktop-only">
							<total-price></total-price>
							${when(this.settingsActive, () => html`
								<login-pop-up></login-pop-up>`)}
							<toolbar-button .icon=${gear} .active=${this.settingsActive} .action=${this.toggleSettings}></toolbar-button>
							${when(!this.specsheetActive && !this.forceOverview && !this.hideOverview && currentSectionConfig.notInDashboard !== true,
								() => html`
									<toolbar-button .icon=${overview} .active=${this.dashboardActive}
													.action=${this.toggleDashboard.bind(this)}></toolbar-button>
								`)
							}
							<toolbar-button .icon=${previous} .disabled=${currentIndex === 0}
											.action=${this.previousSection.bind(this)}></toolbar-button>
							<toolbar-button .icon=${next}
											.disabled=${currentIndex >= this.lastSatisfiedIndex || !this.isInitialized}
											.label=${msg('Next')}
											.action=${this.nextSection.bind(this)}></toolbar-button>
							<toolbar-button class="finalize"
											.inverted=${true}
											.icon=${specsheet}
											.disabled=${this.lastSatisfiedIndex < this.sectionConfig.length - 1 || currentIndex >= this.lastSatisfiedIndex || !this.isInitialized}
											.label=${msg('Finalize')}
											.action=${this.onFinalize.bind(this)}></toolbar-button>
						</div>
						`,
					() => html`
						<toolbar-button .icon=${previous} .disabled=${currentIndex === 0}
										.action=${this.previousSection.bind(this)}></toolbar-button>
					`)}
					</div>
				`)
			}
		`;
	}
}
